import { reactPlugin } from "appInsightsWeb";
import type { BrandTwoLetterCode, RegionCode } from "brandRegionConfig";
import type { SupportedLocale } from "staticConfiguration";
import {
  AppInsightsContext,
  AppInsightsErrorBoundary,
} from "@microsoft/applicationinsights-react-js";
import { Box, type ThemeOptions } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ApplicationLoadingDisplay } from "core/components/ApplicationLoadingDisplay";
import { ConfigCheckWrapper } from "core/components/ConfigCheckWrapper";
import { ConfirmProvider } from "core/components/ConfirmProvider";
import { DatePickerLocalizationProvider } from "core/components/DatePickerLocalizationProvider";
import { MUIThemeAndLocalizationProvider } from "core/components/MUIThemeAndLocalizationProvider";
import { Toaster } from "core/components/Toaster";
import { AvailableRegionsContextProvider } from "core/context/AvailableRegionsContext";
import { BrandContext } from "core/context/BrandContextProvider";
import { LocaleContext } from "core/context/LocaleContext";
import { ApplicationErrorBoundaryPage } from "core/pages/ApplicationErrorBoundaryPage";
import Head from "next/head";
import { useRouter } from "next/router";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import type React from "react";
import { Suspense, useMemo } from "react";
import BrandMap from "resources/BrandMap";
import { SSRWrapper } from "./SSRWrapper";

/**
 * SharedWrapper component provides a common wrapper for the application,
 * handling global configurations and providers such as:
 * - AppInsights error boundary and context
 * - MUI theme and localization
 * - Date picker localization
 * - Available regions context
 * - Global styles and CSS baseline
 * - Toaster for notifications
 * - Configuration checks
 *
 * @param children - The child components to be wrapped
 * @param brandTwoLetterCode - The brand identifier for theming and configuration
 * @param availableRegions - Array of available region codes
 * @param customTheme - Optional custom MUI theme options
 */
export function SharedAppWrapper({
  children,
  brandTwoLetterCode,
  availableRegions = [],
  customTheme,
  skipConfigCheck = false,
}: {
  children: React.ReactNode;
  brandTwoLetterCode: BrandTwoLetterCode;
  availableRegions?: RegionCode[];
  customTheme?: ThemeOptions | null;
  skipConfigCheck?: boolean;
}) {
  const { locale } = useRouter();
  const brand = useMemo(() => {
    return BrandMap.get(brandTwoLetterCode) || null;
  }, [brandTwoLetterCode]);
  if (!brand) {
    throw new Error(
      `Brand not found for two-letter code "${brandTwoLetterCode}"`
    );
  }
  return (
    <BrandContext.Provider value={brand}>
      <LocaleContext.Provider value={locale as SupportedLocale}>
        <MUIThemeAndLocalizationProvider customTheme={customTheme}>
          <CssBaseline />
          <SSRWrapper>
            <Suspense
              // Suspense is triggered when loading a translation resource
              fallback={<ApplicationLoadingDisplay />}
            >
              <Head>
                <link
                  rel="icon"
                  href={`/favicons/${brandTwoLetterCode}.png`}
                  type="image/png"
                />
              </Head>
              <NuqsAdapter>
                <AvailableRegionsContextProvider
                  availableRegions={availableRegions}
                >
                  <ConfigCheckWrapper skip={skipConfigCheck}>
                    <AppInsightsContext.Provider value={reactPlugin}>
                      <AppInsightsErrorBoundary
                        appInsights={reactPlugin}
                        onError={ApplicationErrorBoundaryPage}
                      >
                        <DatePickerLocalizationProvider>
                          <Toaster>
                            <ConfirmProvider>
                              <Box
                                sx={{
                                  backgroundColor: "background.default",
                                  minHeight: "100vh",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                {children}
                              </Box>
                            </ConfirmProvider>
                          </Toaster>
                        </DatePickerLocalizationProvider>
                      </AppInsightsErrorBoundary>
                    </AppInsightsContext.Provider>
                  </ConfigCheckWrapper>
                </AvailableRegionsContextProvider>
              </NuqsAdapter>
            </Suspense>
          </SSRWrapper>
        </MUIThemeAndLocalizationProvider>
      </LocaleContext.Provider>
    </BrandContext.Provider>
  );
}
